import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiData from '../../services/api_data';

// Async thunks
export const getNewsletters = createAsyncThunk(
    'newsletter/getNewsletters',
    async (clientId, { rejectWithValue }) => {
        try {
            const data = await apiData.getNewsletters(clientId);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getNewsletterById = createAsyncThunk(
    'newsletter/getNewsletterById',
    async (newsletterId, { rejectWithValue }) => {
        try {
            const data = await apiData.getNewsletterById(newsletterId);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const createNewsletter = createAsyncThunk(
    'newsletter/createNewsletter',
    async (newsletterData, { rejectWithValue }) => {
        try {
            const data = await apiData.createNewsletter(newsletterData);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const updateNewsletter = createAsyncThunk(
    'newsletter/updateNewsletter',
    async ({ id, newsletterData }, { rejectWithValue }) => {
        try {
            const data = await apiData.updateNewsletter(id, newsletterData);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const deleteNewsletter = createAsyncThunk(
    'newsletter/deleteNewsletter',
    async (newsletterId, { rejectWithValue }) => {
        try {
            await apiData.deleteNewsletter(newsletterId);
            return newsletterId;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const addItemsToNewsletter = createAsyncThunk(
    'newsletter/addItems',
    async ({ newsletterId, items }, { rejectWithValue }) => {
        try {
            const data = await apiData.addNewsletterItems(newsletterId, items);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const removeItemFromNewsletter = createAsyncThunk(
    'newsletter/removeItem',
    async ({ newsletterId, itemId }, { rejectWithValue }) => {
        try {
            const data = await apiData.removeNewsletterItem(newsletterId, itemId);
            return { itemId, data };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const reorderNewsletterItems = createAsyncThunk(
    'newsletter/reorderItems',
    async ({ newsletterId, items }, { rejectWithValue }) => {
        try {
            const data = await apiData.reorderNewsletterItems(newsletterId, items);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getNewsletterPreview = createAsyncThunk(
    'newsletter/getPreview',
    async (newsletterId, { rejectWithValue }) => {
        try {
            const data = await apiData.getNewsletterPreview(newsletterId);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const sendTestNewsletter = createAsyncThunk(
    'newsletter/sendTest',
    async ({ newsletterId, emails }, { rejectWithValue }) => {
        try {
            const data = await apiData.sendTestNewsletter(newsletterId, emails);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const sendNewsletter = createAsyncThunk(
    'newsletter/send',
    async (newsletterId, { rejectWithValue }) => {
        try {
            const data = await apiData.sendNewsletter(newsletterId);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getNewsletterRecipients = createAsyncThunk(
    'newsletter/getRecipients',
    async (clientId, { rejectWithValue }) => {
        try {
            const data = await apiData.getNewsletterRecipients(clientId);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const addNewsletterRecipient = createAsyncThunk(
    'newsletter/addRecipient',
    async ({ clientId, recipient }, { rejectWithValue }) => {
        try {
            const data = await apiData.addNewsletterRecipient(clientId, recipient);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const removeNewsletterRecipient = createAsyncThunk(
    'newsletter/removeRecipient',
    async ({ clientId, recipientId }, { rejectWithValue }) => {
        try {
            await apiData.removeNewsletterRecipient(clientId, recipientId);
            return recipientId;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const newsletterSlice = createSlice({
    name: 'newsletter',
    initialState: {
        newsletters: [],
        currentNewsletter: null,
        recipients: [],
        preview: null,
        loading: false,
        error: null,
        sendStatus: 'idle', // idle, sending, sent, error
    },
    reducers: {
        clearCurrentNewsletter: (state) => {
            state.currentNewsletter = null;
        },
        clearPreview: (state) => {
            state.preview = null;
        },
        clearError: (state) => {
            state.error = null;
        },
        resetSendStatus: (state) => {
            state.sendStatus = 'idle';
        },
        updateCurrentNewsletterItems: (state, action) => {
            if (state.currentNewsletter) {
                state.currentNewsletter.items = action.payload;
            }
        },
    },
    extraReducers: (builder) => {
        // Get newsletters
        builder.addCase(getNewsletters.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getNewsletters.fulfilled, (state, action) => {
            state.loading = false;
            state.newsletters = action.payload;
        });
        builder.addCase(getNewsletters.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Get newsletter by ID
        builder.addCase(getNewsletterById.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getNewsletterById.fulfilled, (state, action) => {
            state.loading = false;
            state.currentNewsletter = action.payload;
        });
        builder.addCase(getNewsletterById.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Create newsletter
        builder.addCase(createNewsletter.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(createNewsletter.fulfilled, (state, action) => {
            state.loading = false;
            state.currentNewsletter = action.payload;
            state.newsletters.push(action.payload);
        });
        builder.addCase(createNewsletter.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Update newsletter
        builder.addCase(updateNewsletter.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(updateNewsletter.fulfilled, (state, action) => {
            state.loading = false;
            state.currentNewsletter = action.payload;
            const index = state.newsletters.findIndex(n => n.id === action.payload.id);
            if (index !== -1) {
                state.newsletters[index] = action.payload;
            }
        });
        builder.addCase(updateNewsletter.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Delete newsletter
        builder.addCase(deleteNewsletter.fulfilled, (state, action) => {
            state.newsletters = state.newsletters.filter(n => n.id !== action.payload);
            if (state.currentNewsletter?.id === action.payload) {
                state.currentNewsletter = null;
            }
        });

        // Add items
        builder.addCase(addItemsToNewsletter.fulfilled, (state, action) => {
            state.currentNewsletter = action.payload;
        });

        // Remove item - don't update currentNewsletter since backend only returns { success: true }
        // Local state is already updated in the component
        builder.addCase(removeItemFromNewsletter.fulfilled, (state) => {
            // No-op - local state handles the removal
        });

        // Reorder items - don't update currentNewsletter since backend only returns { success: true }
        // Local state is already updated optimistically in the component
        builder.addCase(reorderNewsletterItems.fulfilled, (state) => {
            // No-op - local state handles the reorder
        });

        // Get preview
        builder.addCase(getNewsletterPreview.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getNewsletterPreview.fulfilled, (state, action) => {
            state.loading = false;
            state.preview = action.payload;
        });
        builder.addCase(getNewsletterPreview.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Send test
        builder.addCase(sendTestNewsletter.pending, (state) => {
            state.sendStatus = 'sending';
            state.error = null;
        });
        builder.addCase(sendTestNewsletter.fulfilled, (state) => {
            state.sendStatus = 'sent';
        });
        builder.addCase(sendTestNewsletter.rejected, (state, action) => {
            state.sendStatus = 'error';
            state.error = action.payload;
        });

        // Send newsletter
        builder.addCase(sendNewsletter.pending, (state) => {
            state.sendStatus = 'sending';
            state.error = null;
        });
        builder.addCase(sendNewsletter.fulfilled, (state, action) => {
            state.sendStatus = 'sent';
            if (state.currentNewsletter) {
                state.currentNewsletter.status = 'sent';
                state.currentNewsletter.sentAt = new Date().toISOString();
            }
            const index = state.newsletters.findIndex(n => n.id === action.payload.id);
            if (index !== -1) {
                state.newsletters[index] = action.payload;
            }
        });
        builder.addCase(sendNewsletter.rejected, (state, action) => {
            state.sendStatus = 'error';
            state.error = action.payload;
        });

        // Get recipients
        builder.addCase(getNewsletterRecipients.fulfilled, (state, action) => {
            state.recipients = action.payload;
        });

        // Add recipient - normalize isBcc from response (any casing) or from request
        builder.addCase(addNewsletterRecipient.fulfilled, (state, action) => {
            const res = action.payload;
            const sent = action.meta?.arg?.recipient;
            const isBcc = res?.isBcc ?? res?.IsBcc ?? res?.is_bcc ?? sent?.isBcc ?? false;
            state.recipients.push({ ...res, isBcc });
        });

        // Remove recipient
        builder.addCase(removeNewsletterRecipient.fulfilled, (state, action) => {
            state.recipients = state.recipients.filter(r => r.id !== action.payload);
        });
    },
});

export const { 
    clearCurrentNewsletter, 
    clearPreview, 
    clearError, 
    resetSendStatus,
    updateCurrentNewsletterItems 
} = newsletterSlice.actions;

export default newsletterSlice.reducer;

